import { useMemo } from 'react';
import isToday from 'date-fns/isToday';
import { paymentType as PaymentType } from '@ourbranch/lookups';
import { isFarmInstallmentPlan } from '@ourbranch/farm-utils';

import { awsDateToStringDate, dateFormatter } from 'core/helpers/formatters';

export const DATE_FORMAT = 'MM/dd/yyyy';
export const AWS_DATE_FORMAT = 'yyyy-MM-dd';

function getDownPaymentDate(effectiveDateStr) {
  const effectiveDate = new Date(effectiveDateStr);
  return isToday(new Date(effectiveDate)) ? 'TODAY' : dateFormatter(effectiveDate);
}

const useDetailedPrice = (offer, policyType) => {
  return useMemo(() => {
    if (!offer || !offer.options) {
      return {};
    }

    const {
      homeownersPaymentType,
      autoPaymentType,
      autoEffectiveDate,
      rentersPaymentType,
      condoPaymentType,
      farmPaymentType,
      homeMegaDownPay,
      autoMegaDownPay,
      rentersMegaDownPay,
      condoMegaDownPay,
      farmMegaDownPay
    } = offer.quote.global;

    const isHomeMonthly = homeownersPaymentType === PaymentType.Monthly;
    // Installment plans: Monthly, and the CFM auto 2-payment plan (#36200).
    const isAutoMonthly = [PaymentType.Monthly, PaymentType.HalfTerm].includes(autoPaymentType);
    const isRentersMonthly = rentersPaymentType === PaymentType.Monthly;
    const isCondoMonthly = condoPaymentType === PaymentType.Monthly;
    // Shared with the farm Quote/Application Payment Plan section (#36434).
    const isFarmMonthly = isFarmInstallmentPlan(farmPaymentType);

    let {
      homeBillingDayOfMonth,
      autoBillingDayOfMonth,
      rentersBillingDayOfMonth,
      rentersEffectiveDate,
      condoBillingDayOfMonth,
      farmBillingDayOfMonth
    } = offer.quote.global;

    const {
      homeTotal,
      autoTotal,
      autoCoverages,
      rentersTotal,
      condoTotal,
      farmTotal,
      homeSchedule,
      autoSchedule,
      rentersSchedule,
      condoSchedule,
      farmSchedule
    } = offer.options.find((x) => x.type === policyType) || {};

    // we need a billing day to figure out the payment amount in the case of monhtly down payment, so we choose today or at latest
    if (!homeBillingDayOfMonth) {
      homeBillingDayOfMonth = Math.min(new Date().getDate(), 28);
    }

    if (!autoBillingDayOfMonth) {
      autoBillingDayOfMonth = Math.min(new Date().getDate(), 28);
    }

    if (!rentersBillingDayOfMonth) {
      rentersBillingDayOfMonth = Math.min(new Date().getDate(), 28);
    }

    if (!condoBillingDayOfMonth) {
      condoBillingDayOfMonth = Math.min(new Date().getDate(), 28);
    }

    if (!farmBillingDayOfMonth) {
      farmBillingDayOfMonth = Math.min(new Date().getDate(), 28);
    }

    // older offers may not have this
    if (!rentersEffectiveDate) {
      rentersEffectiveDate = autoEffectiveDate;
    }

    const prices = {};
    if (isHomeMonthly && homeSchedule) {
      const [firstPayment, ...remainingPayments] = homeSchedule;

      prices.homeDownPaymentDate = firstPayment.date;
      prices.homeRemainingPayments = remainingPayments.length;
      prices.homePrice = firstPayment.remainingMonthly;
      prices.homeRemainingPaymentsAmount = remainingPayments[0].remainingMonthly;
      prices.homeNextPayments = homeSchedule;
      prices.homeDownPayment = homeMegaDownPay
        ? firstPayment.remainingMonthly - remainingPayments[0].remainingMonthly
        : firstPayment.remainingMonthly;
    } else {
      prices.homePrice = homeTotal;
    }

    if (isAutoMonthly && autoSchedule) {
      const [firstPayment, ...remainingPayments] = autoSchedule;

      prices.autoDownPaymentDate = firstPayment.date;
      prices.autoRemainingPayments = remainingPayments.length;
      prices.autoPrice = firstPayment.remainingMonthly;
      prices.autoRemainingPaymentsAmount = remainingPayments[0]?.remainingMonthly;
      prices.autoNextPayments = autoSchedule;
      prices.autoDownPayment = autoMegaDownPay
        ? firstPayment.remainingMonthly - remainingPayments[0]?.remainingMonthly
        : firstPayment.remainingMonthly;
      // CFM auto card fee (#36200), as for farm: remainingMonthly already includes the per-charge fee,
      // so expose it and the fee-free base for the "$base + $fee CC fees" disclosure. Auto without a
      // card fee (Branch) leaves the base unset so the disclosure shows the plain price.
      prices.autoPriceTransactionFee = firstPayment.transactionFee ?? 0;
      if (prices.autoPriceTransactionFee) {
        prices.autoPriceBase = firstPayment.remainingMonthly - prices.autoPriceTransactionFee;
      }
      prices.autoRemainingPaymentsTransactionFee = remainingPayments[0]?.transactionFee ?? 0;
      if (prices.autoRemainingPaymentsTransactionFee) {
        prices.autoRemainingPaymentsBase =
          remainingPayments[0].remainingMonthly - prices.autoRemainingPaymentsTransactionFee;
      }
    } else {
      prices.autoPrice = autoTotal;
      prices.autoDownPaymentDate = 'TODAY';
      // One-time auto: no schedule, so the card fee comes from the option's credit_card fee line.
      prices.autoPriceTransactionFee =
        offer.options.find((x) => x.type === policyType)?.autoFees?.find((f) => f?.type === 'credit_card')?.amount ?? 0;
      if (prices.autoPriceTransactionFee) {
        prices.autoPriceBase = autoTotal - prices.autoPriceTransactionFee;
      }
    }

    if (isCondoMonthly && condoSchedule) {
      const [firstPayment, ...remainingPayments] = condoSchedule;

      prices.condoPrice = firstPayment.remainingMonthly;
      prices.condoRemainingPaymentsAmount = remainingPayments[0].remainingMonthly;
      prices.condoRemainingPayments = remainingPayments.length;
      prices.condoDownPaymentDate = firstPayment.date;
      prices.condoNextPayments = condoSchedule;
      prices.condoDownPayment = condoMegaDownPay
        ? firstPayment.remainingMonthly - remainingPayments[0].remainingMonthly
        : firstPayment.remainingMonthly;
    } else {
      prices.condoPrice = condoTotal;
    }

    if (isFarmMonthly && farmSchedule) {
      const [firstPayment, ...remainingPayments] = farmSchedule;

      // remainingMonthly already includes the CC fee share (via farmTotal); transactionFee
      // is the per-charge CC portion exposed for disclosure text / breakdown UI. farmPriceBase
      // is derived for surfaces that show "$base + $fee CC fees".
      prices.farmDownPaymentDate = firstPayment.date;
      prices.farmRemainingPayments = remainingPayments.length;
      prices.farmPrice = firstPayment.remainingMonthly;
      prices.farmPriceTransactionFee = firstPayment.transactionFee ?? 0;
      prices.farmPriceBase = firstPayment.remainingMonthly - (firstPayment.transactionFee ?? 0);
      prices.farmRemainingPaymentsAmount = remainingPayments[0]?.remainingMonthly;
      prices.farmRemainingPaymentsTransactionFee = remainingPayments[0]?.transactionFee ?? 0;
      prices.farmRemainingPaymentsBase =
        (remainingPayments[0]?.remainingMonthly ?? 0) - (remainingPayments[0]?.transactionFee ?? 0);
      prices.farmNextPayments = farmSchedule;
      prices.farmDownPayment = farmMegaDownPay
        ? firstPayment.remainingMonthly - remainingPayments[0]?.remainingMonthly
        : firstPayment.remainingMonthly;
    } else {
      // OneTime farm: no installment schedule, but the CC fee (when applicable — e.g. direct
      // billing with a CC first installment) still needs to surface in the disclosure breakdown.
      const farmCcFee =
        offer.options.find((x) => x.type === policyType)?.farmFees?.find((f) => f?.type === 'credit_card')?.amount ?? 0;
      prices.farmPrice = farmTotal;
      prices.farmPriceTransactionFee = farmCcFee;
      prices.farmPriceBase = farmTotal - farmCcFee;
    }

    if (isRentersMonthly && rentersSchedule) {
      const [firstPayment, ...remainingPayments] = rentersSchedule;

      prices.rentersDownPaymentDate = firstPayment.date;
      prices.rentersRemainingPayments = remainingPayments.length;
      prices.rentersPrice = firstPayment.remainingMonthly;
      prices.rentersRemainingPaymentsAmount = remainingPayments[0].remainingMonthly;
      prices.rentersNextPayments = rentersSchedule;
      prices.rentersDownPayment = rentersMegaDownPay
        ? firstPayment.remainingMonthly - remainingPayments[0].remainingMonthly
        : firstPayment.remainingMonthly;
    } else {
      prices.rentersPrice = rentersTotal;
      // use a fallback
      prices.rentersPaymentDate = rentersEffectiveDate
        ? getDownPaymentDate(awsDateToStringDate(rentersEffectiveDate))
        : undefined;
    }

    if (policyType === 'AR' && autoCoverages) {
      const rentersAmount = autoCoverages
        .filter((c) => c.type === 'renters_total')
        .reduce((acc, c) => {
          return acc + c.amount;
        }, 0);
      const autoAmount = autoTotal - rentersAmount;
      prices.rentersTotalAR = autoPaymentType === PaymentType.Monthly ? rentersAmount / 6 : rentersAmount;
      prices.autoTotalAR = autoPaymentType === PaymentType.Monthly ? autoAmount / 6 : autoAmount;
    }

    return prices;
  }, [offer, policyType]);
};

export default useDetailedPrice;
